// src/components/Hero/Hero.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaRocket,
  FaChartLine,
  FaUsers,
  FaCheckCircle,
  FaArrowRight,
  FaPlayCircle,
  FaGlobe,
  FaClock,
} from "react-icons/fa";
import "./Hero.css";
import { useLocalContext } from "../../context/LocalContext";

const Hero = () => {
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();
  const brandName = webinfo?.name || "Your Studio";

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.05 },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <section
      className="hero3"
      aria-label={`${brandName} growth studio hero section`}
    >
      <div className="hero3-bg" aria-hidden="true" />
      <div className="hero3-noise" aria-hidden="true" />

      <div className="hero3-shell">
        {/* Top bar */}
        <motion.div
          className="hero3-top"
          initial="hidden"
          animate="show"
          variants={container}
        >
          <motion.div className="hero3-pill" variants={fadeUp}>
            <span className="hero3-pill-dot" />
            <span className="hero3-pill-label">
              Growth studio for service, SaaS & e-commerce brands
            </span>
          </motion.div>

          <motion.div className="hero3-top-meta" variants={fadeUp}>
            <div className="hero3-top-meta-item">
              <FaGlobe aria-hidden="true" />
              <span>Clients across 5+ countries</span>
            </div>
            <div className="hero3-top-meta-item">
              <FaClock aria-hidden="true" />
              <span>Most projects ramp in 4–6 weeks</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Main split */}
        <motion.div
          className="hero3-main"
          initial="hidden"
          animate="show"
          variants={container}
        >
          {/* LEFT: Story + proof + CTAs */}
          <motion.div className="hero3-left" variants={fadeUp}>
            <h1 className="hero3-heading">
              <span className="hero3-heading-kicker">
                Stop duct-taping tools and campaigns.
              </span>
              <span className="hero3-heading-main">
                Build a marketing engine{" "}
                <span className="hero3-heading-gradient">
                  that compounds over time.
                </span>
              </span>
            </h1>

            <p className="hero3-body">
              {brandName} helps you connect strategy, websites, funnels, ads,
              email and analytics into a single growth system—so every campaign
              you run makes the next one easier, cheaper and more profitable.
            </p>

            {/* Proof row */}
            <div className="hero3-proof-row" aria-label="Highlights">
              <div className="hero3-proof-item">
                <FaChartLine aria-hidden="true" />
                <div>
                  <span className="hero3-proof-k">3–12 month growth plans</span>
                  <span className="hero3-proof-d">
                    Not just one-off launches or ad hoc fixes.
                  </span>
                </div>
              </div>
              <div className="hero3-proof-item">
                <FaUsers aria-hidden="true" />
                <div>
                  <span className="hero3-proof-k">Embedded with your team</span>
                  <span className="hero3-proof-d">
                    Strategy, execution & reporting under one partner.
                  </span>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="hero3-actions">
              <button
                className="hx-btn hx-btn-primary"
                onClick={() => navigate("/contact")}
                aria-label="Schedule an intro call"
              >
                Schedule an intro call
                <FaArrowRight aria-hidden="true" />
              </button>
              <button
                className="hx-btn hx-btn-ghost"
                onClick={() => navigate("/services")}
                aria-label="See services and pricing"
              >
                See services & pricing
                <FaPlayCircle aria-hidden="true" />
              </button>
            </div>

            {/* Social proof / avatars */}
            <div className="hero3-trust">
              <div className="hero3-avatars" aria-hidden="true">
                <span className="hero3-avatar hero3-avatar--one" />
                <span className="hero3-avatar hero3-avatar--two" />
                <span className="hero3-avatar hero3-avatar--three" />
              </div>
              <div className="hero3-trust-copy">
                <p>
                  <strong>20+ active accounts</strong> supported in the last
                  year, from lean founders to in-house marketing teams.
                </p>
                <p className="hero3-trust-sub">
                  We plug into your stack, not the other way around.
                </p>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Dashboard card + “engagement modes” */}
          <motion.div className="hero3-right" variants={fadeUp}>
            {/* Live dashboard style card */}
            <div className="hero3-card hero3-card--dashboard">
              <div className="hero3-card-header">
                <div>
                  <p className="hero3-card-kicker">Live growth overview</p>
                  <h2>How your engine performs this quarter</h2>
                </div>
                <div className="hero3-card-tag">
                  <span className="hero3-card-tag-dot" />
                  All numbers pulled from your real data stack
                </div>
              </div>

              <div className="hero3-kpi-grid">
                <div className="hero3-kpi">
                  <span className="hero3-kpi-label">Pipeline value</span>
                  <span className="hero3-kpi-value">₹48.2L</span>
                  <span className="hero3-kpi-sub">3.4× monthly revenue goal</span>
                </div>
                <div className="hero3-kpi">
                  <span className="hero3-kpi-label">New qualified leads</span>
                  <span className="hero3-kpi-value hero3-kpi-value--up">
                    +61%
                  </span>
                  <span className="hero3-kpi-sub">vs previous 90 days</span>
                </div>
                <div className="hero3-kpi">
                  <span className="hero3-kpi-label">Cost per lead</span>
                  <span className="hero3-kpi-value hero3-kpi-value--down">
                    –27%
                  </span>
                  <span className="hero3-kpi-sub">after landing page refresh</span>
                </div>
              </div>

              {/* Fake mini-chart */}
              <div className="hero3-chart">
                <div className="hero3-chart-grid" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="hero3-chart-line" aria-hidden="true">
                  <span className="hero3-chart-point" />
                  <span className="hero3-chart-point" />
                  <span className="hero3-chart-point" />
                  <span className="hero3-chart-point" />
                  <span className="hero3-chart-point" />
                </div>
                <div className="hero3-chart-footer">
                  <span>Month 1</span>
                  <span>Month 2</span>
                  <span>Month 3</span>
                  <span>Month 4</span>
                </div>
              </div>

              {/* Experiments list */}
              <div className="hero3-experiments">
                <p className="hero3-experiments-title">
                  Next experiments already queued:
                </p>
                <ul>
                  <li>
                    <FaCheckCircle aria-hidden="true" />
                    New offer angle test for high-intent segments
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" />
                    Post-purchase email & upsell flow optimisation
                  </li>
                  <li>
                    <FaCheckCircle aria-hidden="true" />
                    Landing page A/B test for mobile traffic
                  </li>
                </ul>
              </div>
            </div>

            {/* Ways of working card */}
            <div className="hero3-card hero3-card--modes">
              <p className="hero3-card-kicker">How we work together</p>
              <div className="hero3-modes-grid">
                <div className="hero3-mode">
                  <FaRocket aria-hidden="true" />
                  <h3>Launch & rebuild sprints</h3>
                  <p>
                    Ideal when you need a new website, funnel or offer live
                    within 4–8 weeks with messaging, design and tracking aligned.
                  </p>
                </div>
                <div className="hero3-mode">
                  <FaChartLine aria-hidden="true" />
                  <h3>Ongoing growth partner</h3>
                  <p>
                    Monthly retainers where we manage ads, CRO, email and
                    reporting as part of your extended team.
                  </p>
                </div>
                <div className="hero3-mode">
                  <FaUsers aria-hidden="true" />
                  <h3>Team enablement</h3>
                  <p>
                    Audits, playbooks and dashboards your internal team can keep
                    running without us.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom capability strip */}
        <div className="hero3-strip">
          <div className="hero3-strip-item">
            <span className="hero3-strip-label">Acquisition</span>
            <span className="hero3-strip-meta">
              Paid search & social · SEO foundations · Landing pages
            </span>
          </div>
          <div className="hero3-strip-divider" />
          <div className="hero3-strip-item">
            <span className="hero3-strip-label">Conversion & retention</span>
            <span className="hero3-strip-meta">
              CRO tests · Email & SMS · Nurture journeys
            </span>
          </div>
          <div className="hero3-strip-divider" />
          <div className="hero3-strip-item">
            <span className="hero3-strip-label">Data & decision-making</span>
            <span className="hero3-strip-meta">
              Analytics setup · Dashboards · Reporting rituals
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
